import ResourceService from 'src/modules/resource/resourceService';
import Errors from 'src/modules/shared/error/errors';

const prefix = 'RESOURCE_INLINE_EDIT';
const READ_MODE_STORAGE_KEY = 'resource.viewMode';

const readPersistedReadMode = (): boolean => {
  // Default to read mode (true) when no preference is persisted.
  // The inline-edit toggle is currently hidden in the toolbar because
  // RenderItem.tsx does not wire the native inputs to commitValue, so
  // edit mode produces no actual saves while still triggering the
  // backend lock acquisition. Defaulting to read mode prevents wasted
  // lock requests for users who never explicitly opted into edit mode.
  // Re-enable opt-in by restoring the toolbar Switch (see ResourceViewPage.tsx).
  try {
    if (typeof window === 'undefined') return true;
    const stored = window.localStorage.getItem(
      READ_MODE_STORAGE_KEY,
    );
    if (stored === null) return true;
    return stored === 'read';
  } catch {
    return true;
  }
};

const writePersistedReadMode = (value: boolean) => {
  try {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(
      READ_MODE_STORAGE_KEY,
      value ? 'read' : 'edit',
    );
  } catch {
    // best-effort persistence
  }
};

const resourceInlineEditActions = {
  STORAGE_KEY: READ_MODE_STORAGE_KEY,

  SAVE_STARTED: `${prefix}_SAVE_STARTED`,
  SAVE_SUCCESS: `${prefix}_SAVE_SUCCESS`,
  SAVE_ERROR: `${prefix}_SAVE_ERROR`,
  SAVE_IDLE: `${prefix}_SAVE_IDLE`,
  READ_MODE_TOGGLED: `${prefix}_READ_MODE_TOGGLED`,
  READ_MODE_HYDRATED: `${prefix}_READ_MODE_HYDRATED`,

  doHydrateReadMode: () => (dispatch) => {
    dispatch({
      type: resourceInlineEditActions.READ_MODE_HYDRATED,
      payload: { readMode: readPersistedReadMode() },
    });
  },

  doToggleReadMode:
    (readMode: boolean) => (dispatch) => {
      writePersistedReadMode(readMode);
      dispatch({
        type: resourceInlineEditActions.READ_MODE_TOGGLED,
        payload: { readMode },
      });
    },

  doResetSaveStatus: () => (dispatch) => {
    dispatch({
      type: resourceInlineEditActions.SAVE_IDLE,
    });
  },

  doUpdateField:
    (id: string, data: Record<string, unknown>) =>
    async (dispatch) => {
      try {
        dispatch({
          type: resourceInlineEditActions.SAVE_STARTED,
        });
        await ResourceService.update(id, data);
        dispatch({
          type: resourceInlineEditActions.SAVE_SUCCESS,
          payload: { savedAt: Date.now() },
        });
      } catch (error) {
        Errors.handle(error);
        dispatch({
          type: resourceInlineEditActions.SAVE_ERROR,
          payload: {
            error: (error as Error)?.message ?? null,
          },
        });
      }
    },
};

export default resourceInlineEditActions;
